/**
 * ScheduledTasksPage.jsx
 * 
 * Admin interface for managing Scheduled Tasks using the existing ScheduledTask model.
 * Tasks are executed by the ScheduledTasksService background service.
 * 
 * Supported task types:
 * - CreateActionItem: Creates action items on schedule for users/departments
 * - RunSqlReport: Executes SQL queries and emails results
 * - GeneratePdfReport: Generates and sends PDF reports
 * 
 * Features:
 * - CRUD operations for scheduled tasks
 * - Cron expression builder with presets
 * - Task-specific configuration forms
 * - Enable/disable tasks
 * - Execution history and status tracking
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import '@/styles/ScheduledTasksStyles.scss';
import { FaSpinner, FaCalendarAlt, FaPlus, FaEdit, FaTrash, FaTag, FaClock, FaFolder, FaExclamationTriangle, FaPlusCircle, FaTimes, FaSave, FaShieldAlt, FaExclamationCircle, FaPlay, FaPause, FaShareAlt } from 'react-icons/fa';
import * as Sentry from '@sentry/react';

/**
 * Get common cron expression presets for easy scheduling.
 * Presets are automatically converted to UTC based on user's timezone.
 * @returns {Array} - Array of preset objects
 */
const getCronPresets = () => {
    const localHour = 8; // 8:00 AM local time
    const utcHour = convertLocalHourToUTC(localHour);
    
    return [
        { label: 'Every hour', value: '0 * * * *', description: 'Every hour' },
        { label: 'Every day at 8:00 AM', value: `0 ${utcHour} * * *`, description: 'Daily at 8 AM' },
        { label: 'Every Monday at 8:00 AM', value: `0 ${utcHour} * * 1`, description: 'Weekly on Monday' },
        { label: 'Every weekday at 8:00 AM', value: `0 ${utcHour} * * 1-5`, description: 'Mon-Fri at 8 AM' },
        { label: 'First of every month at 8:00 AM', value: `0 ${utcHour} 1 * *`, description: 'Monthly on the 1st' },
        { label: '15th of every month at 8:00 AM', value: `0 ${utcHour} 15 * *`, description: 'Monthly on the 15th' },
        { label: 'Every quarter (1st Jan, Apr, Jul, Oct)', value: `0 ${utcHour} 1 1,4,7,10 *`, description: 'Quarterly' },
        { label: 'Custom schedule...', value: 'custom', description: 'Build your own schedule' }
    ];
};

/**
 * Days of week options for the schedule builder.
 */
const DAYS_OF_WEEK = [
    { value: '0', label: 'Sunday', short: 'Sun' },
    { value: '1', label: 'Monday', short: 'Mon' },
    { value: '2', label: 'Tuesday', short: 'Tue' },
    { value: '3', label: 'Wednesday', short: 'Wed' },
    { value: '4', label: 'Thursday', short: 'Thu' },
    { value: '5', label: 'Friday', short: 'Fri' },
    { value: '6', label: 'Saturday', short: 'Sat' }
];

/**
 * Frequency options for the schedule builder.
 */
const FREQUENCY_OPTIONS = [
    { value: 'hourly', label: 'Every Hour' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'custom', label: 'Advanced (Cron)' }
];

/**
 * Gets the current timezone offset in hours.
 * @returns {number} - Timezone offset in hours (e.g., 2 for UTC+2)
 */
const getTimezoneOffsetHours = () => {
    return -new Date().getTimezoneOffset() / 60;
};

/**
 * Gets a human-readable timezone string (e.g., "UTC+2", "UTC-5").
 * @returns {string} - Timezone string
 */
const getTimezoneString = () => {
    const offset = getTimezoneOffsetHours();
    if (offset === 0) return 'UTC';
    const sign = offset > 0 ? '+' : '';
    return `UTC${sign}${offset}`;
};

/**
 * Converts local hour to UTC hour for cron expression.
 * The backend scheduler interprets cron expressions in UTC, so we need to convert
 * the user's local time selection to UTC.
 * @param {number} localHour - Hour in local timezone (0-23)
 * @returns {number} - Hour in UTC timezone (0-23)
 */
const convertLocalHourToUTC = (localHour) => {
    const offset = getTimezoneOffsetHours();
    let utcHour = localHour - offset;
    
    // Handle day wraparound
    if (utcHour < 0) utcHour += 24;
    if (utcHour >= 24) utcHour -= 24;
    
    return utcHour;
};

/**
 * Converts UTC hour to local hour for display.
 * @param {number} utcHour - Hour in UTC timezone (0-23)
 * @returns {number} - Hour in local timezone (0-23)
 */
const convertUTCHourToLocal = (utcHour) => {
    const offset = getTimezoneOffsetHours();
    let localHour = utcHour + offset;
    
    // Handle day wraparound
    if (localHour < 0) localHour += 24;
    if (localHour >= 24) localHour -= 24;
    
    return localHour;
};

/**
 * Task type definitions with metadata.
 */
const TASK_TYPES = {
    CreateActionItem: {
        name: 'Create Action Item',
        description: 'Creates action items on a recurring schedule',
        icon: 'fa-tasks',
        category: 'Action Items'
    },
    RunSqlReport: {
        name: 'SQL Report',
        description: 'Executes SQL queries and emails results',
        icon: 'fa-database',
        category: 'Reports'
    },
    GeneratePdfReport: {
        name: 'PDF Report',
        description: 'Generates and sends PDF reports',
        icon: 'fa-file-pdf',
        category: 'Reports'
    }
};

/**
 * Priority options for action items.
 */
const PRIORITY_OPTIONS = ['Low', 'Medium', 'High', 'Critical'];

/**
 * Assignment type options for action items.
 */
const ASSIGNMENT_TYPES = [
    { value: 'User', label: 'Specific User' },
    { value: 'Users', label: 'Multiple Users' },
    { value: 'Department', label: 'Entire Department' },
    { value: 'PropertyRole', label: 'Property Role' }
];

/**
 * Output format options for reports.
 */
const OUTPUT_FORMATS = [
    { value: 'CSV', label: 'CSV' },
    { value: 'XLSX', label: 'Excel (XLSX)' }
];

/**
 * ScheduledTasksPage Component
 * Main component for managing scheduled tasks.
 */
const ScheduledTasksPage = () => {
    const { apiFetch } = useAuth();
    
    // Tasks state
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingTask, setEditingTask] = useState(null);
    const [selectedTaskType, setSelectedTaskType] = useState(null);
    const [downloadingTaskIds, setDownloadingTaskIds] = useState(new Set());
    
    // Reference data
    const [templates, setTemplates] = useState([]);
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [users, setUsers] = useState([]);
    const [properties, setProperties] = useState([]);
    const [databases, setDatabases] = useState([]);
    
    // Filter state
    const [showDisabled, setShowDisabled] = useState(false);
    const [filterTaskType, setFilterTaskType] = useState('');

    const getDatabaseTypeForValidation = useCallback((databaseSecretKey) => {
        if (!databaseSecretKey || !databases) {
            return null;
        }

        const selectedDb = databases.find(db => db.secretKey === databaseSecretKey);
        return selectedDb?.secretType || null;
    }, [databases]);

    const validateSqlQuery = useCallback(async (query, databaseSecretKey) => {
        if (!query || !query.trim()) {
            return { isValid: false, error: 'SQL query is required' };
        }

        try {
            const response = await apiFetch('/api/ScheduledTasks/validate-sql', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    sqlQuery: query,
                    databaseType: getDatabaseTypeForValidation(databaseSecretKey)
                })
            });

            if (!response.ok) {
                return { isValid: false, error: 'SQL validation failed' };
            }

            const result = await response.json();
            if (result.success && result.data) {
                return {
                    isValid: !!result.data.isValid,
                    error: result.data.error || null
                };
            }

            return { isValid: false, error: result.message || 'SQL validation failed' };
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error validating SQL query:', error);
            return { isValid: false, error: 'SQL validation failed' };
        }
    }, [apiFetch, getDatabaseTypeForValidation]);

    // Get current username
    const currentUserName = localStorage.getItem('displayName') || 'System';

    /**
     * Fetch scheduled tasks from API.
     */
    const fetchTasks = useCallback(async () => {
        try {
            setLoading(true);
            let url = `/api/ScheduledTasks?includeDisabled=${showDisabled}`;
            if (filterTaskType) {
                url += `&taskType=${filterTaskType}`;
            }
            
            const response = await apiFetch(url);
            const data = await response.json();
            
            if (data.success) {
                setTasks(data.data || []);
            } else {
                toast.error(data.message || 'Failed to load scheduled tasks');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching scheduled tasks:', error);
            toast.error('Error loading scheduled tasks');
        } finally {
            setLoading(false);
        }
    }, [apiFetch, showDisabled, filterTaskType]);

    /**
     * Fetch reference data (templates, departments, users, etc.).
     */
    const fetchReferenceData = useCallback(async () => {
        try {
            // Fetch templates
            const templatesRes = await apiFetch('/api/templates');
            if (templatesRes.ok) {
                const templatesData = await templatesRes.json();
                if (templatesData.success) {
                    setTemplates(templatesData.data || []);
                }
            }

            // Fetch departments
            const deptsRes = await apiFetch('/api/Department');
            if (deptsRes.ok) {
                const deptsData = await deptsRes.json();
                if (deptsData.success) {
                    setDepartments(deptsData.data || []);
                }
            }

            // Fetch users
            const usersRes = await apiFetch('/api/User/all');
            if (usersRes.ok) {
                const usersData = await usersRes.json();
                if (usersData.success) {
                    // Map API response properties (id, name) to expected names (userID, displayName)
                    setUsers((usersData.data || []).map(u => ({
                        userID: u.id,
                        displayName: u.name,
                        email: u.email
                    })));
                }
            }

            // Fetch properties
            const propsRes = await apiFetch('/api/Property/all');
            if (propsRes.ok) {
                const propsData = await propsRes.json();
                if (propsData.success) {
                    setProperties(propsData.data || []);
                }
            }

            // Fetch available databases - try dedicated endpoint first, fallback to secrets
            try {
                const dbRes = await apiFetch('/api/ScheduledTasks/available-databases');
                const dbData = await dbRes.json();
                if (dbData.success && dbData.data && dbData.data.length > 0) {
                    setDatabases(dbData.data);
                } else {
                    // Fallback: fetch from AppSecrets API and filter for database connection strings
                    const secretsRes = await apiFetch('/api/AppSecrets');
                    const secretsData = await secretsRes.json();
                    if (secretsData.success && secretsData.data) {
                        // Filter secrets that are database connections (MsSqlConnection or MySqlConnection types)
                        // or have SQL/DB in their key name
                        const dbSecrets = secretsData.data.filter(s => 
                            s.secretType === 'MsSqlConnection' ||
                            s.secretType === 'MySqlConnection' ||
                            s.secretType?.toLowerCase().includes('sql') ||
                            s.secretType?.toLowerCase().includes('database') ||
                            s.secretKey?.toLowerCase().includes('sql') ||
                            s.secretKey?.toLowerCase().includes('db') ||
                            s.secretKey?.toLowerCase().includes('connection')
                        ).map(s => ({
                            secretKey: s.secretKey,
                            displayName: s.displayName || s.secretKey,
                            secretType: s.secretType || 'Database'
                        }));
                        setDatabases(dbSecrets);
                    }
                }
            } catch (dbError) {
                Sentry.captureException(dbError);
                console.error('Error fetching databases:', dbError);
                // Try secrets fallback
                try {
                    const secretsRes = await apiFetch('/api/AppSecrets');
                    const secretsData = await secretsRes.json();
                    if (secretsData.success && secretsData.data) {
                        // Filter secrets that are database connections
                        const dbSecrets = secretsData.data.filter(s => 
                            s.secretType === 'MsSqlConnection' ||
                            s.secretType === 'MySqlConnection' ||
                            s.secretType?.toLowerCase().includes('sql') ||
                            s.secretType?.toLowerCase().includes('database') ||
                            s.secretKey?.toLowerCase().includes('sql') ||
                            s.secretKey?.toLowerCase().includes('db') ||
                            s.secretKey?.toLowerCase().includes('connection')
                        ).map(s => ({
                            secretKey: s.secretKey,
                            displayName: s.displayName || s.secretKey,
                            secretType: s.secretType || 'Database'
                        }));
                        setDatabases(dbSecrets);
                    }
                } catch (secretsError) {
                    Sentry.captureException(secretsError);
                    console.error('Error fetching secrets for databases:', secretsError);
                }
            }

            // Fetch email templates for scheduled reports
            const emailTplRes = await apiFetch('/api/EmailTemplates?category=Reports');
            const emailTplData = await emailTplRes.json();
            if (emailTplData.success) {
                setEmailTemplates(emailTplData.data || []);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching reference data:', error);
        }
    }, [apiFetch]);

    // Load data on mount
    useEffect(() => {
        (async () => { await fetchReferenceData(); })();
    }, [fetchReferenceData]);

    useEffect(() => {
        (async () => { await fetchTasks(); })();
    }, [fetchTasks]);

    /**
     * Handle creating new task - show task type selection first.
     */
    const handleCreateTask = () => {
        setEditingTask(null);
        setSelectedTaskType(null);
        setShowModal(true);
    };

    /**
     * Handle selecting task type for new task.
     */
    const handleSelectTaskType = (taskType) => {
        setSelectedTaskType(taskType);
    };

    /**
     * Handle editing existing task.
     */
    const handleEditTask = async (task) => {
        try {
            // Fetch full task details including configuration
            const response = await apiFetch(`/api/ScheduledTasks/${task.id}`);
            const data = await response.json();
            
            if (data.success) {
                setEditingTask(data.data);
                setSelectedTaskType(data.data.taskType);
                setShowModal(true);
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching task details:', error);
            toast.error('Error loading task details');
        }
    };

    /**
     * Handle toggling task enabled status.
     */
    const handleToggleTask = async (task) => {
        try {
            const response = await apiFetch(
                `/api/ScheduledTasks/${task.id}/toggle?modifiedBy=${encodeURIComponent(currentUserName)}`,
                { method: 'POST' }
            );
            const data = await response.json();
            
            if (data.success) {
                toast.success(data.message);
                fetchTasks();
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error toggling task:', error);
            toast.error('Error toggling task status');
        }
    };

    /**
     * Handle deleting task.
     */
    const handleDeleteTask = async (task) => {
        if (!confirm(`Are you sure you want to delete "${task.name}"?`)) return;
        
        try {
            const response = await apiFetch(
                `/api/ScheduledTasks/${task.id}`,
                { method: 'DELETE' }
            );
            const data = await response.json();
            
            if (data.success) {
                toast.success(data.message);
                fetchTasks();
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error deleting task:', error);
            toast.error('Error deleting task');
        }
    };

    /**
 * Handle saving the CSV report for a RunSqlReport task directly to SharePoint.
 * The backend executes the SQL query and uploads the result to the configured
 * SharePoint folder — no file-picker dialog is shown.
 */
    const handleSaveToSharePoint = useCallback(async (task) => {
        setDownloadingTaskIds(prev => new Set([...prev, task.id]));
        try {
            const response = await apiFetch(`/api/ScheduledTasks/${task.id}/save-to-sharepoint`, {
                method: 'POST'
            });
            const data = await response.json();

            if (data.success) {
                if (data.fileUrl) {
                    toast.success(
                        <span>
                            Saved to SharePoint.{' '}
                            <a href={data.fileUrl} target="_blank" rel="noreferrer"
                                style={{ color: '#fff', textDecoration: 'underline' }}>
                                View file
                            </a>
                        </span>
                    );
                } else {
                    toast.success('Report saved to SharePoint successfully');
                }
            } else {
                toast.error(data.message || 'Failed to save to SharePoint');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error saving report to SharePoint:', error);
            toast.error('Error saving report to SharePoint');
        } finally {
            setDownloadingTaskIds(prev => {
                const next = new Set(prev);
                next.delete(task.id);
                return next;
            });
        }
    }, [apiFetch]);

    /**
     * Handle saving task (create or update).
     */
    const handleSaveTask = async (taskData) => {
        try {
            const payload = {
                ...taskData,
                createdBy: currentUserName,
                modifiedBy: currentUserName
            };

            let response;
            if (editingTask) {
                response = await apiFetch(
                    `/api/ScheduledTasks/${editingTask.id}`,
                    {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(payload)
                    }
                );
            } else {
                response = await apiFetch(
                    '/api/ScheduledTasks',
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(payload)
                    }
                );
            }

            const data = await response.json();
            
            if (data.success) {
                toast.success(data.message);
                setShowModal(false);
                setSelectedTaskType(null);
                fetchTasks();
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error saving task:', error);
            toast.error('Error saving scheduled task');
        }
    };

    /**
     * Close modal and reset state.
     */
    const handleCloseModal = () => {
        setShowModal(false);
        setEditingTask(null);
        setSelectedTaskType(null);
    };

    /**
     * Format date for display.
     */
    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        return date.toLocaleString('en-ZA', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    /**
     * Get task type info.
     */
    const getTaskTypeInfo = (taskType) => {
        return TASK_TYPES[taskType] || { name: taskType, icon: 'fa-cog', category: 'Other' };
    };

    /**
     * Convert a cron expression to a human-readable schedule description.
     */
    const describeCron = (cron) => {
        if (!cron) return 'Not scheduled';
        const parts = cron.trim().split(/\s+/);
        if (parts.length !== 5) return cron;

        const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

        // Guard: only build time string when hour and minute are numeric.
        // Non-numeric tokens (e.g. "*/5", "*") indicate interval-based schedules
        // that cannot be represented as a simple time.
        const hourNum = parseInt(hour);
        const minuteNum = parseInt(minute);
        const hasNumericTime = !isNaN(hourNum) && !isNaN(minuteNum);

        const buildTimeStr = () => {
            const localHour = convertUTCHourToLocal(hourNum);
            return `${String(localHour).padStart(2, '0')}:${String(minuteNum).padStart(2, '0')}`;
        };

        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const monthNames = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

        // Interval-based patterns (e.g. "*/5 * * * *" = every 5 minutes)
        if (!hasNumericTime) {
            if (minute.startsWith('*/') && hour === '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
                return `Every ${minute.slice(2)} minutes`;
            }
            if (hour.startsWith('*/') && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
                return `Every ${hour.slice(2)} hours`;
            }
            return cron;
        }

        const timeStr = buildTimeStr();

        // Every day
        if (dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
            return `Daily at ${timeStr}`;
        }
        // Specific days of week
        if (dayOfMonth === '*' && month === '*' && dayOfWeek !== '*') {
            const days = dayOfWeek.split(',').map(d => {
                if (d.includes('-')) {
                    const [start, end] = d.split('-').map(Number);
                    return `${dayNames[start]}-${dayNames[end]}`;
                }
                return dayNames[parseInt(d)] || d;
            }).join(', ');
            return `${days} at ${timeStr}`;
        }
        // Specific day of month
        if (dayOfMonth !== '*' && dayOfWeek === '*') {
            const dayNum = dayOfMonth;
            const suffix = dayNum === '1' || dayNum === '21' || dayNum === '31' ? 'st' :
                          dayNum === '2' || dayNum === '22' ? 'nd' :
                          dayNum === '3' || dayNum === '23' ? 'rd' : 'th';
            if (month !== '*') {
                const months = month.split(',').map(m => monthNames[parseInt(m)] || m).join(', ');
                return `${dayNum}${suffix} of ${months} at ${timeStr}`;
            }
            return `${dayNum}${suffix} of every month at ${timeStr}`;
        }
        return cron;
    };

    /**
     * Get status badge class and label.
     */
    const getStatusDisplay = (task) => {
        if (!task.isEnabled) return { label: 'Disabled', className: 'status-disabled' };
        if (task.lastRunStatus === 'Failed') return { label: 'Failed', className: 'status-failed' };
        if (task.lastRunStatus === 'Success') return { label: 'Success', className: 'status-success' };
        if (task.lastRunStatus === 'Running') return { label: 'Running', className: 'status-running' };
        return { label: 'Never run', className: 'status-pending' };
    };

    /**
     * Render the tasks list as a professional table.
     */
    const renderTasksList = () => {
        if (loading) {
            return (
                <div className="loading-state">
                    <FaSpinner className="fa-spin fa-2x" />
                    <p>Loading scheduled tasks...</p>
                </div>
            );
        }

        if (tasks.length === 0) {
            return (
                <div className="empty-state">
                    <FaCalendarAlt className="fa-3x" />
                    <h3>No Scheduled Tasks</h3>
                    <p>Create your first scheduled task to automate recurring operations.</p>
                    <button className="btn btn-primary" onClick={handleCreateTask}>
                        <FaPlus /> Create Scheduled Task
                    </button>
                </div>
            );
        }

        return (
            <div className="tasks-table-wrapper">
                <table className="tasks-table">
                    <thead>
                        <tr>
                            <th className="col-status">Status</th>
                            <th className="col-name">Task Name</th>
                            <th className="col-type">Type</th>
                            <th className="col-schedule">Schedule</th>
                            <th className="col-category">Category</th>
                            <th className="col-last-run">Last Run</th>
                            <th className="col-next-run">Next Run</th>
                            <th className="col-actions">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tasks.map(task => {
                            const typeInfo = getTaskTypeInfo(task.taskType);
                            const status = getStatusDisplay(task);
                            return (
                                <tr key={task.id} className={!task.isEnabled ? 'row-disabled' : ''}>
                                    <td className="col-status">
                                        <span className={`status-badge ${status.className}`}>
                                            {status.label}
                                        </span>
                                    </td>
                                    <td className="col-name">
                                        <div className="task-name-cell">
                                            <span className="task-name">{task.name}</span>
                                            {task.description && (
                                                <span className="task-description">{task.description}</span>
                                            )}
                                            {task.lastRunError && (
                                                <span className="task-error">
                                                    <FaExclamationTriangle /> {task.lastRunError}
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="col-type">
                                        <span className="type-badge">
                                            <FaTag /> {typeInfo.name}
                                        </span>
                                    </td>
                                    <td className="col-schedule">
                                        <span className="schedule-text">{describeCron(task.cronExpression)}</span>
                                    </td>
                                    <td className="col-category">
                                        {task.category ? (
                                            <span className="category-badge">
                                                <FaFolder /> {task.category}
                                            </span>
                                        ) : (
                                            <span className="text-muted">—</span>
                                        )}
                                    </td>
                                    <td className="col-last-run">
                                        <span className="date-text">{formatDate(task.lastRunAt)}</span>
                                    </td>
                                    <td className="col-next-run">
                                        <span className="date-text">{formatDate(task.nextRunAt)}</span>
                                    </td>
                                    <td className="col-actions">
                                        <div className="action-buttons">
                                            <button
                                                className="btn-action btn-edit"
                                                onClick={() => handleEditTask(task)}
                                                title="Edit task"
                                            >
                                                <FaEdit />
                                            </button>
                                            <button
                                                className={`btn-action ${task.isEnabled ? 'btn-pause' : 'btn-enable'}`}
                                                onClick={() => handleToggleTask(task)}
                                                title={task.isEnabled ? 'Disable task' : 'Enable task'}
                                            >
                                                {task.isEnabled ? <FaPause /> : <FaPlay />}
                                            </button>
                                            {task.taskType === 'RunSqlReport' && (
                                                <button
                                                    className="btn-action btn-download"
                                                    onClick={() => handleSaveToSharePoint(task)}
                                                    title="Save to SharePoint"
                                                    disabled={downloadingTaskIds.has(task.id)}
                                                >
                                                    {downloadingTaskIds.has(task.id)
                                                        ? <FaSpinner className="fa-spin" />
                                                        : <FaShareAlt />}
                                                </button>
                                            )}
                                            <button
                                                className="btn-action btn-delete"
                                                onClick={() => handleDeleteTask(task)}
                                                title="Delete task"
                                            >
                                                <FaTrash />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div className="scheduled-tasks-page">

            {/* Page Header */}
            <div className="page-header">
                <div className="header-content">
                    <h1>
                        <FaCalendarAlt /> Scheduled Tasks
                    </h1>
                    <p className="page-description">
                        Configure automated recurring tasks like action item creation and scheduled reports.
                    </p>
                </div>

                <div className="header-actions">
                    <button className="btn btn-primary" onClick={handleCreateTask}>
                        <FaPlus /> New Scheduled Task
                    </button>
                </div>
            </div>

            {/* Summary Stats Bar */}
            <div className="tasks-summary-bar">
                <div className="summary-stat">
                    <span className="summary-count">{tasks.length}</span>
                    <span className="summary-label">Total Tasks</span>
                </div>
                <div className="summary-stat">
                    <span className="summary-count success">{tasks.filter(t => t.isEnabled && t.lastRunStatus === 'Success').length}</span>
                    <span className="summary-label">Healthy</span>
                </div>
                <div className="summary-stat">
                    <span className="summary-count danger">{tasks.filter(t => t.lastRunStatus === 'Failed').length}</span>
                    <span className="summary-label">Failed</span>
                </div>
                <div className="summary-stat">
                    <span className="summary-count muted">{tasks.filter(t => !t.isEnabled).length}</span>
                    <span className="summary-label">Disabled</span>
                </div>
                <div className="summary-filters">
                    <label className="checkbox-label">
                        <input
                            type="checkbox"
                            checked={showDisabled}
                            onChange={(e) => setShowDisabled(e.target.checked)}
                        />
                        Show Disabled
                    </label>
                    <select
                        className="filter-select"
                        value={filterTaskType}
                        onChange={(e) => setFilterTaskType(e.target.value)}
                    >
                        <option value="">All Task Types</option>
                        {Object.entries(TASK_TYPES).map(([type, info]) => (
                            <option key={type} value={type}>{info.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Content */}
            <div className="tasks-content">
                {renderTasksList()}
            </div>

            {/* Task Modal */}
            {showModal && (
                <TaskModal
                    task={editingTask}
                    taskType={selectedTaskType}
                    onSelectTaskType={handleSelectTaskType}
                    templates={templates}
                    emailTemplates={emailTemplates}
                    departments={departments}
                    users={users}
                    properties={properties}
                    databases={databases}
                    apiFetch={apiFetch}
                    onValidateSql={validateSqlQuery}
                    onSave={handleSaveTask}
                    onClose={handleCloseModal}
                />
            )}
        </div>
    );
};

/**
 * TaskModal Component
 * Modal for creating/editing scheduled tasks.
 */
const TaskModal = ({ task, taskType, onSelectTaskType, templates, emailTemplates, departments, users, properties, databases, apiFetch, onValidateSql, onSave, onClose }) => {
    const [formData, setFormData] = useState({
        name: task?.name || '',
        description: task?.description || '',
        taskType: taskType || task?.taskType || '',
        cronExpression: task?.cronExpression || '0 8 * * 1',
        configuration: task?.configuration ? JSON.parse(task.configuration) : {},
        isEnabled: task?.isEnabled ?? true,
        category: task?.category || ''
    });

    // Determine if the current cron expression matches a preset
    const getInitialPreset = () => {
        const currentCron = task?.cronExpression || `0 ${convertLocalHourToUTC(8)} * * 1`;
        const matchingPreset = getCronPresets().find(p => p.value === currentCron && p.value !== 'custom');
        return matchingPreset ? currentCron : 'custom';
    };

    const [selectedPreset, setSelectedPreset] = useState(getInitialPreset());
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    
    // Schedule builder state for user-friendly schedule creation
    const [scheduleBuilder, setScheduleBuilder] = useState({
        frequency: 'weekly',
        hour: 8,
        minute: 0,
        dayOfMonth: 1,
        selectedDays: ['1'], // Monday by default
        useBuilder: selectedPreset === 'custom'
    });

    // If no task type selected yet, show task type selection
    if (!taskType && !task) {
        return (
            <div className="modal-backdrop" onClick={onClose}>
                <div className="modal-content" onClick={e => e.stopPropagation()}>
                    <div className="modal-header">
                        <h2>
                            <FaPlusCircle />
                            Select Task Type
                        </h2>
                        <button className="close-btn" onClick={onClose}>
                            <FaTimes />
                        </button>
                    </div>

                    <div className="modal-body">
                        <div className="task-type-grid">
                            {Object.entries(TASK_TYPES).map(([type, info]) => (
                                <div 
                                    key={type}
                                    className="task-type-card"
                                    onClick={() => onSelectTaskType(type)}
                                >
                                    <i className={`fas ${info.icon} fa-2x`}></i>
                                    <h3>{info.name}</h3>
                                    <p>{info.description}</p>
                                    <span className="category-badge">{info.category}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const typeInfo = TASK_TYPES[taskType] || { name: taskType, icon: 'fa-cog' };

    /**
     * Handle form field change.
     */
    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    /**
     * Handle configuration field change.
     */
    const handleConfigChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            configuration: { ...prev.configuration, [field]: value }
        }));
    };

    /**
     * Handle cron preset selection.
     */
    const handlePresetChange = (preset) => {
        setSelectedPreset(preset);
        if (preset === 'custom') {
            // When selecting custom, enable the builder and use builder-generated cron
            setScheduleBuilder(prev => ({ ...prev, useBuilder: true }));
        } else {
            // When selecting a preset, disable builder and use preset value
            setScheduleBuilder(prev => ({ ...prev, useBuilder: false }));
            setFormData(prev => ({ ...prev, cronExpression: preset }));
        }
    };

    /**
     * Handle schedule builder field change and update cron expression.
     */
    const handleScheduleBuilderChange = (field, value) => {
        setScheduleBuilder(prev => {
            const updated = { ...prev, [field]: value };
            // Rebuild cron expression when builder settings change
            if (field !== 'useBuilder' && updated.useBuilder && updated.frequency !== 'custom') {
                const { frequency: freq, hour: h, minute: m, dayOfMonth: dom, selectedDays: days } = updated;
                const minuteStr = m.toString();
                // Convert local time to UTC for cron expression
                const utcHour = convertLocalHourToUTC(h);
                const hourStr = utcHour.toString();
                
                let newCron;
                if (freq === 'hourly') {
                    newCron = '0 * * * *';
                } else if (freq === 'daily') {
                    newCron = `${minuteStr} ${hourStr} * * *`;
                } else if (freq === 'weekly') {
                    const daysStr = days.length > 0 ? days.sort().join(',') : '1';
                    newCron = `${minuteStr} ${hourStr} * * ${daysStr}`;
                } else if (freq === 'monthly') {
                    newCron = `${minuteStr} ${hourStr} ${dom} * *`;
                } else {
                    newCron = formData.cronExpression;
                }
                setFormData(prevForm => ({ ...prevForm, cronExpression: newCron }));
            }
            return updated;
        });
    };

    /**
     * Toggle a day in the weekly schedule.
     */
    const toggleDay = (dayValue) => {
        setScheduleBuilder(prev => {
            const newDays = prev.selectedDays.includes(dayValue)
                ? prev.selectedDays.filter(d => d !== dayValue)
                : [...prev.selectedDays, dayValue];
            
            const updated = { ...prev, selectedDays: newDays };
            
            // Rebuild cron if using builder for weekly
            if (prev.useBuilder && prev.frequency === 'weekly') {
                const daysStr = newDays.length > 0 ? newDays.sort().join(',') : '1';
                // Convert local time to UTC for cron expression
                const utcHour = convertLocalHourToUTC(prev.hour);
                const newCron = `${prev.minute} ${utcHour} * * ${daysStr}`;
                setFormData(prevForm => ({ ...prevForm, cronExpression: newCron }));
            }
            
            return updated;
        });
    };

    /**
     * Get human-readable description of current schedule.
     * Shows times in local timezone for clarity.
     * @returns {string} - Human readable schedule description
     */
    const getScheduleDescription = () => {
        const cron = formData.cronExpression;
        const parts = cron.split(' ');
        if (parts.length !== 5) return 'Invalid schedule';

        const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;
        // Convert UTC hour to local hour for display
        const localHour = convertUTCHourToLocal(parseInt(hour) || 0);
        const timeStr = `${localHour.toString().padStart(2, '0')}:${minute.padStart(2, '0')} ${getTimezoneString()}`;

        if (hour === '*') {
            return 'Every hour';
        }
        if (dayOfWeek === '*' && dayOfMonth === '*' && month === '*') {
            return `Every day at ${timeStr}`;
        }
        if (dayOfWeek !== '*' && dayOfMonth === '*' && month === '*') {
            const days = dayOfWeek.split(',').map(d => {
                const dayInfo = DAYS_OF_WEEK.find(dw => dw.value === d);
                return dayInfo ? dayInfo.short : d;
            }).join(', ');
            return `Every ${days} at ${timeStr}`;
        }
        if (dayOfMonth !== '*' && month === '*') {
            return `Day ${dayOfMonth} of every month at ${timeStr}`;
        }
        return `Cron: ${cron} (UTC)`;
    };

    /**
     * Handle testing the task immediately.
     */
    const handleTest = async () => {
        // Validate SQL query if task type is RunSqlReport.
        if (taskType === 'RunSqlReport' && formData.configuration.sqlQuery) {
            const sqlValidation = await onValidateSql(formData.configuration.sqlQuery, formData.configuration.databaseSecretKey);
            if (!sqlValidation.isValid) {
                toast.error(sqlValidation.error);
                return;
            }
        }

        setTesting(true);
        try {
            const response = await apiFetch('/api/ScheduledTasks/test', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    taskType: taskType,
                    configuration: JSON.stringify(formData.configuration)
                })
            });

            if (response.ok) {
                const result = await response.json();
                
                if (result.success) {
                    toast.success(result.message || 'Task executed successfully!');
                } else {
                    toast.error(result.message || 'Task execution failed');
                }
            } else {
                toast.error('Failed to test task');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error testing task:', error);
            toast.error('Error testing task: ' + error.message);
        } finally {
            setTesting(false);
        }
    };

    /**
     * Handle form submission.
     */
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.name.trim()) {
            toast.error('Name is required');
            return;
        }
        
        if (!formData.cronExpression.trim()) {
            toast.error('Schedule is required');
            return;
        }

        // Validate SQL query if task type is RunSqlReport
        if (taskType === 'RunSqlReport' && formData.configuration.sqlQuery) {
            const sqlValidation = await onValidateSql(formData.configuration.sqlQuery, formData.configuration.databaseSecretKey);
            if (!sqlValidation.isValid) {
                toast.error(sqlValidation.error);
                return;
            }
        }

        setSaving(true);
        try {
            await onSave({
                name: formData.name,
                description: formData.description,
                taskType: taskType,
                cronExpression: formData.cronExpression,
                configuration: JSON.stringify(formData.configuration),
                isEnabled: formData.isEnabled,
                category: formData.category || typeInfo.category
            });
        } finally {
            setSaving(false);
        }
    };

    /**
     * Render task-specific configuration fields.
     */
    const renderConfigurationFields = () => {
        switch (taskType) {
            case 'CreateActionItem':
                return (
                    <CreateActionItemConfig 
                        config={formData.configuration}
                        onChange={handleConfigChange}
                        templates={templates}
                        departments={departments}
                        users={users}
                        properties={properties}
                        apiFetch={apiFetch}
                    />
                );
            case 'RunSqlReport':
                return (
                    <RunSqlReportConfig
                        config={formData.configuration}
                        onChange={handleConfigChange}
                        onValidateSql={onValidateSql}
                        databases={databases}
                        emailTemplates={emailTemplates}
                    />
                );
            case 'GeneratePdfReport':
                return (
                    <GeneratePdfReportConfig
                        config={formData.configuration}
                        onChange={handleConfigChange}
                    />
                );
            default:
                return (
                    <div className="form-group">
                        <label>Configuration (JSON)</label>
                        <textarea
                            value={JSON.stringify(formData.configuration, null, 2)}
                            onChange={(e) => {
                                try {
                                    const parsed = JSON.parse(e.target.value);
                                    setFormData(prev => ({ ...prev, configuration: parsed }));
                                } catch (parseError) {
                                    Sentry.captureException(parseError);
                                    // Ignore JSON parse errors in the UI during user input,
                                    // but log them at debug level for developer inspection.
                                    // This allows partial/incomplete JSON while typing
                                    console.debug('JSON parse error (expected during editing):', parseError.message);
                                }
                            }}
                            rows={6}
                            className="code-input"
                        />
                    </div>
                );
        }
    };

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div className="modal-content large-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>
                        <i className={`fas ${typeInfo.icon}`}></i>
                        {task ? `Edit ${typeInfo.name}` : `New Scheduled ${typeInfo.name}`}
                    </h2>
                    <button className="close-btn" onClick={onClose}>
                        <FaTimes />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="modal-body">
                        {/* Basic Info Section */}
                        <div className="form-section">
                            <h3>Basic Information</h3>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>Task Name *</label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => handleChange('name', e.target.value)}
                                        placeholder="e.g., Weekly Safety Inspection"
                                        required
                                    />
                                    <small>A name to identify this scheduled task</small>
                                </div>
                                <div className="form-group">
                                    <label>Category</label>
                                    <input
                                        type="text"
                                        value={formData.category}
                                        onChange={(e) => handleChange('category', e.target.value)}
                                        placeholder={typeInfo.category}
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Description</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => handleChange('description', e.target.value)}
                                    placeholder="Describe the purpose of this scheduled task..."
                                    rows={2}
                                />
                            </div>
                        </div>

                        {/* Task-specific Configuration — shown before Schedule for better flow */}
                        <div className="form-section">
                            <h3>{taskType === 'CreateActionItem' ? 'Action Item Setup' : 'Configuration'}</h3>
                            {renderConfigurationFields()}
                        </div>

                        {/* Schedule Section */}
                        <div className="form-section">
                            <h3>Schedule</h3>

                            <div className="form-group">
                                <label>Schedule Preset</label>
                                <select
                                    value={selectedPreset}
                                    onChange={(e) => handlePresetChange(e.target.value)}
                                >
                                    {getCronPresets().map(p => (
                                        <option key={p.value} value={p.value}>{p.label}</option>
                                    ))}
                                </select>
                            </div>

                            {/* User-friendly Schedule Builder when Custom is selected */}
                            {selectedPreset === 'custom' && (
                                <div className="schedule-builder">
                                    <div className="form-group">
                                        <label>Frequency</label>
                                        <select
                                            value={scheduleBuilder.frequency}
                                            onChange={(e) => handleScheduleBuilderChange('frequency', e.target.value)}
                                        >
                                            {FREQUENCY_OPTIONS.map(f => (
                                                <option key={f.value} value={f.value}>{f.label}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Time picker for all frequencies */}
                                    {scheduleBuilder.frequency !== 'custom' && (
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label>Hour (0-23)</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max="23"
                                                    value={scheduleBuilder.hour}
                                                    onChange={(e) => handleScheduleBuilderChange('hour', parseInt(e.target.value) || 0)}
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Minute (0-59)</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max="59"
                                                    value={scheduleBuilder.minute}
                                                    onChange={(e) => handleScheduleBuilderChange('minute', parseInt(e.target.value) || 0)}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {/* Timezone notice */}
                                    {scheduleBuilder.frequency !== 'custom' && (
                                        <div className="timezone-notice" style={{ 
                                            padding: '8px 12px', 
                                            backgroundColor: '#e3f2fd', 
                                            borderLeft: '3px solid #2196f3',
                                            borderRadius: '4px',
                                            marginBottom: '1rem',
                                            fontSize: '0.9rem',
                                            color: '#1565c0'
                                        }}>
                                            <FaClock style={{ marginRight: '6px' }} />
                                            Times are in your local timezone ({getTimezoneString()})
                                        </div>
                                    )}

                                    {/* Day of week selector for weekly */}
                                    {scheduleBuilder.frequency === 'weekly' && (
                                        <div className="form-group">
                                            <label>Run on days</label>
                                            <div className="day-selector">
                                                {DAYS_OF_WEEK.map(day => (
                                                    <button
                                                        key={day.value}
                                                        type="button"
                                                        className={`day-btn ${scheduleBuilder.selectedDays.includes(day.value) ? 'selected' : ''}`}
                                                        onClick={() => toggleDay(day.value)}
                                                    >
                                                        {day.short}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Day of month selector for monthly */}
                                    {scheduleBuilder.frequency === 'monthly' && (
                                        <div className="form-group">
                                            <label>Day of Month (1-28)</label>
                                            <input
                                                type="number"
                                                min="1"
                                                max="28"
                                                value={scheduleBuilder.dayOfMonth}
                                                onChange={(e) => handleScheduleBuilderChange('dayOfMonth', parseInt(e.target.value) || 1)}
                                            />
                                            <small>Using 1-28 ensures the schedule runs every month</small>
                                        </div>
                                    )}

                                    {/* Advanced cron input */}
                                    {scheduleBuilder.frequency === 'custom' && (
                                        <div className="form-group">
                                            <label>Cron Expression</label>
                                            <input
                                                type="text"
                                                value={formData.cronExpression}
                                                onChange={(e) => handleChange('cronExpression', e.target.value)}
                                                placeholder="0 8 * * 1"
                                            />
                                            <small>Format: minute hour day-of-month month day-of-week</small>
                                        </div>
                                    )}

                                    {/* Schedule preview */}
                                    <div className="schedule-preview">
                                        <FaClock />
                                        <span><strong>Schedule:</strong> {getScheduleDescription()}</span>
                                    </div>
                                </div>
                            )}

                            {/* Show schedule description for presets too */}
                            {selectedPreset !== 'custom' && (
                                <div className="schedule-preview">
                                    <FaClock />
                                    <span><strong>Schedule:</strong> {getCronPresets().find(p => p.value === selectedPreset)?.description || getScheduleDescription()}</span>
                                </div>
                            )}

                            <div className="form-group checkbox-group">
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={formData.isEnabled}
                                        onChange={(e) => handleChange('isEnabled', e.target.checked)}
                                    />
                                    Enabled (task will run automatically)
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            Cancel
                        </button>
                        <button 
                            type="button" 
                            className="btn btn-info" 
                            onClick={handleTest}
                            disabled={testing || saving}
                        >
                            {testing ? (
                                <>
                                    <FaSpinner className="fa-spin" /> Testing...
                                </>
                            ) : (
                                <>
                                    <FaPlay /> Test Now
                                </>
                            )}
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={saving}>
                            {saving ? (
                                <>
                                    <FaSpinner className="fa-spin" /> Saving...
                                </>
                            ) : (
                                <>
                                    <FaSave /> {task ? 'Update' : 'Create'} Task
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

/**
 * Placeholder tokens available for action item title and description.
 */
const TITLE_PLACEHOLDERS = [
    { token: '{Date}', label: 'Date', example: '28 November 2025', description: 'Full date' },
    { token: '{ShortDate}', label: 'Short Date', example: '28/11/2025', description: 'Short date format' },
    { token: '{Month}', label: 'Month', example: 'November', description: 'Month name' },
    { token: '{Year}', label: 'Year', example: '2025', description: 'Year' },
    { token: '{Week}', label: 'Week', example: '48', description: 'ISO week number' },
    { token: '{DayOfWeek}', label: 'Day', example: 'Friday', description: 'Day of week' }
];

/**
 * CreateActionItemConfig Component
 * Configuration form for CreateActionItem task type.
 * 
 * Reorganized into logical sections:
 * 1. Action Item Details — what gets created
 * 2. Assignment — who it gets assigned to
 */
const CreateActionItemConfig = ({ config, onChange, templates, departments, users, properties, apiFetch }) => {
    const titleInputRef = React.useRef(null);
    const descInputRef = React.useRef(null);
    const [userSearchTerm, setUserSearchTerm] = React.useState('');
    const [roleUsers, setRoleUsers] = React.useState([]);
    const [loadingRoleUsers, setLoadingRoleUsers] = React.useState(false);

    // Fetch users matching the selected property + role
    React.useEffect(() => {
        const fetchRoleUsers = async () => {
            if (config.assignmentType !== 'PropertyRole' || !config.propertyId || !config.propertyRoleName) {
                setRoleUsers([]);
                return;
            }
            setLoadingRoleUsers(true);
            try {
                const res = await apiFetch(`/api/Property/role-mappings?roleType=${encodeURIComponent(config.propertyRoleName)}`);
                const data = await res.json();
                if (data.success && data.data) {
                    const matched = data.data.filter(m => m.propertyID === config.propertyId && m.isActive !== false);
                    setRoleUsers(matched);
                } else {
                    setRoleUsers([]);
                }
            } catch (catchError) {
                Sentry.captureException(catchError);
                setRoleUsers([]);
            } finally {
                setLoadingRoleUsers(false);
            }
        };
        fetchRoleUsers();
    }, [config.assignmentType, config.propertyId, config.propertyRoleName, apiFetch]);

    /**
     * Insert a placeholder token at the cursor position in the given field.
     */
    const insertPlaceholder = (field, token, ref) => {
        const input = ref.current;
        const currentValue = field === 'actionItemTitle' ? (config.actionItemTitle || '') : (config.actionItemDescription || '');

        if (input && typeof input.selectionStart === 'number') {
            const start = input.selectionStart;
            const end = input.selectionEnd;
            const newValue = currentValue.substring(0, start) + token + currentValue.substring(end);
            onChange(field, newValue);
            // Restore cursor position after the inserted token
            setTimeout(() => {
                input.focus();
                input.setSelectionRange(start + token.length, start + token.length);
            }, 0);
        } else {
            onChange(field, currentValue + token);
        }
    };

    /**
     * Get a live preview of the title with placeholders resolved.
     */
    const getTitlePreview = () => {
        const title = config.actionItemTitle;
        if (!title) return null;

        const now = new Date();
        const weekNumber = getISOWeek(now);

        let preview = title
            .replace(/\{Date\}/g, now.toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' }))
            .replace(/\{ShortDate\}/g, now.toLocaleDateString('en-ZA', { day: '2-digit', month: '2-digit', year: 'numeric' }))
            .replace(/\{Month\}/g, now.toLocaleDateString('en-ZA', { month: 'long' }))
            .replace(/\{Year\}/g, now.getFullYear().toString())
            .replace(/\{Week\}/g, weekNumber.toString())
            .replace(/\{DayOfWeek\}/g, now.toLocaleDateString('en-ZA', { weekday: 'long' }));

        return preview;
    };

    /**
     * Get ISO week number.
     */
    const getISOWeek = (date) => {
        const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
        const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
        return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
    };

    /**
     * Get the due date preview text.
     */
    const getDueDatePreview = () => {
        const days = config.dueDaysFromCreation || 7;
        if (days === 1) return '1 day after creation';
        if (days === 7) return '1 week after creation';
        if (days === 14) return '2 weeks after creation';
        if (days === 30) return '~1 month after creation';
        return `${days} days after creation`;
    };

    /**
     * Handle toggling a user in the multi-select list.
     */
    const toggleUser = (userId) => {
        if (userId == null) return;
        const currentIds = (config.assignedUserIds || '')
            .split(',')
            .map(s => s.trim())
            .filter(Boolean);

        const userIdStr = userId.toString();
        let newIds;
        if (currentIds.includes(userIdStr)) {
            newIds = currentIds.filter(id => id !== userIdStr);
        } else {
            newIds = [...currentIds, userIdStr];
        }
        onChange('assignedUserIds', newIds.join(','));
    };

    /**
     * Check if a user is selected.
     */
    const isUserSelected = (userId) => {
        if (userId == null) return false;
        const currentIds = (config.assignedUserIds || '')
            .split(',')
            .map(s => s.trim())
            .filter(Boolean);
        return currentIds.includes(userId.toString());
    };

    /**
     * Get count of selected users.
     */
    const getSelectedUserCount = () => {
        return (config.assignedUserIds || '')
            .split(',')
            .map(s => s.trim())
            .filter(Boolean)
            .length;
    };

    const titlePreview = getTitlePreview();

    return (
        <>
            {/* ── Section 1: Action Item Details ── */}
            <div className="config-subsection">
                <h4 className="config-subsection-title">
                    <FaEdit className="config-subsection-icon" />
                    Action Item Details
                </h4>
                <p className="config-subsection-desc">Configure what the action item will look like when created.</p>

                <div className="form-group">
                    <label>Template</label>
                    <select
                        value={config.templateKey || ''}
                        onChange={(e) => onChange('templateKey', e.target.value)}
                    >
                        <option value="">No template (blank action item)</option>
                        {templates.map(t => (
                            <option key={t.templateKey} value={t.templateKey}>
                                {t.displayName} ({t.department})
                            </option>
                        ))}
                    </select>
                    <small>Templates pre-fill fields like checklists and default descriptions</small>
                </div>

                <div className="form-group">
                    <label>Action Item Title *</label>
                    <input
                        ref={titleInputRef}
                        type="text"
                        value={config.actionItemTitle || ''}
                        onChange={(e) => onChange('actionItemTitle', e.target.value)}
                        placeholder="e.g., Weekly Safety Check - {Date}"
                        required
                    />
                    <div className="placeholder-buttons">
                        <span className="placeholder-label">Insert:</span>
                        {TITLE_PLACEHOLDERS.map(p => (
                            <button
                                key={p.token}
                                type="button"
                                className="placeholder-btn"
                                onClick={() => insertPlaceholder('actionItemTitle', p.token, titleInputRef)}
                                title={`${p.description} — e.g., "${p.example}"`}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                    {titlePreview && (
                        <div className="title-preview">
                            <span className="title-preview-label">Preview:</span>
                            <span className="title-preview-value">{titlePreview}</span>
                        </div>
                    )}
                </div>

                <div className="form-group">
                    <label>Description</label>
                    <textarea
                        ref={descInputRef}
                        value={config.actionItemDescription || ''}
                        onChange={(e) => onChange('actionItemDescription', e.target.value)}
                        placeholder="Describe the task to be completed..."
                        rows={3}
                    />
                    <div className="placeholder-buttons">
                        <span className="placeholder-label">Insert:</span>
                        {TITLE_PLACEHOLDERS.map(p => (
                            <button
                                key={p.token}
                                type="button"
                                className="placeholder-btn"
                                onClick={() => insertPlaceholder('actionItemDescription', p.token, descInputRef)}
                                title={`${p.description} — e.g., "${p.example}"`}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label>Priority</label>
                        <div className="priority-selector">
                            {PRIORITY_OPTIONS.map(p => (
                                <button
                                    key={p}
                                    type="button"
                                    className={`priority-btn priority-${p.toLowerCase()} ${(config.defaultPriority || 'Medium') === p ? 'selected' : ''}`}
                                    onClick={() => onChange('defaultPriority', p)}
                                >
                                    {p}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="form-group">
                        <label>Due Date</label>
                        <div className="due-date-input">
                            <input
                                type="number"
                                value={config.dueDaysFromCreation || 7}
                                onChange={(e) => onChange('dueDaysFromCreation', parseInt(e.target.value) || null)}
                                min="1"
                                max="365"
                            />
                            <span className="due-date-suffix">days after creation</span>
                        </div>
                        <small className="due-date-preview">{getDueDatePreview()}</small>
                    </div>
                </div>
            </div>

            {/* ── Section 2: Assignment ── */}
            <div className="config-subsection">
                <h4 className="config-subsection-title">
                    <FaTag className="config-subsection-icon" />
                    Assignment
                </h4>
                <p className="config-subsection-desc">Choose who the action item will be assigned to.</p>

                <div className="assignment-type-selector">
                    {ASSIGNMENT_TYPES.map(t => (
                        <button
                            key={t.value}
                            type="button"
                            className={`assignment-type-btn ${(config.assignmentType || 'User') === t.value ? 'selected' : ''}`}
                            onClick={() => onChange('assignmentType', t.value)}
                            title={
                                t.value === 'User' ? 'Assign to one specific person' :
                                t.value === 'Users' ? 'Assign to multiple specific people' :
                                t.value === 'Department' ? 'Assign to every active member of a department' :
                                'Assign to users holding a specific role at a property'
                            }
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                {/* Single user selection with search */}
                {config.assignmentType === 'User' && (() => {
                    const validUsers = users.filter(u => u.userID != null);
                    const selectedUser = validUsers.find(u => u.userID?.toString() === config.assignedUserIds?.toString());
                    const searchResults = userSearchTerm
                        ? validUsers.filter(u =>
                            u.displayName?.toLowerCase().includes(userSearchTerm.toLowerCase()) &&
                            u.userID?.toString() !== config.assignedUserIds?.toString()
                        )
                        : [];
                    return (
                        <div className="form-group">
                            <label>Assign To</label>
                            <div className="user-search-wrapper">
                                <input
                                    type="text"
                                    className="user-search-input"
                                    placeholder="Type a name to search..."
                                    value={userSearchTerm}
                                    onChange={(e) => setUserSearchTerm(e.target.value)}
                                />
                                {userSearchTerm && (
                                    <div className="user-search-dropdown">
                                        {searchResults.map(u => (
                                            <div
                                                key={u.userID}
                                                className="user-search-result"
                                                onClick={() => { onChange('assignedUserIds', u.userID.toString()); setUserSearchTerm(''); }}
                                            >
                                                <FaPlus className="user-add-icon" />
                                                <span>{u.displayName}</span>
                                            </div>
                                        ))}
                                        {searchResults.length === 0 && (
                                            <p className="no-data-message">No matching users</p>
                                        )}
                                    </div>
                                )}
                            </div>
                            {selectedUser && (
                                <div className="user-selected-list">
                                    <div className="user-selected-item">
                                        <span className="user-selected-name">{selectedUser.displayName}</span>
                                        <button
                                            type="button"
                                            className="user-remove-btn"
                                            onClick={() => onChange('assignedUserIds', '')}
                                            title="Remove"
                                        >
                                            <FaTimes />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })()}

                {/* Multiple users selection: search-to-add with selected list */}
                {config.assignmentType === 'Users' && (() => {
                    const validUsers = users.filter(u => u.userID != null);
                    const searchResults = userSearchTerm
                        ? validUsers.filter(u =>
                            u.displayName?.toLowerCase().includes(userSearchTerm.toLowerCase()) &&
                            !isUserSelected(u.userID)
                        )
                        : [];
                    const selectedUsers = validUsers.filter(u => isUserSelected(u.userID));
                    return (
                        <div className="form-group">
                            <div className="user-search-header">
                                <label>Add Users</label>
                                <span className="user-search-count">{getSelectedUserCount()} selected</span>
                            </div>
                            <div className="user-search-wrapper">
                                <input
                                    type="text"
                                    className="user-search-input"
                                    placeholder="Type a name to search..."
                                    value={userSearchTerm}
                                    onChange={(e) => setUserSearchTerm(e.target.value)}
                                />
                                {userSearchTerm && (
                                    <div className="user-search-dropdown">
                                        {searchResults.map(u => (
                                            <div
                                                key={u.userID}
                                                className="user-search-result"
                                                onClick={() => { toggleUser(u.userID); setUserSearchTerm(''); }}
                                            >
                                                <FaPlus className="user-add-icon" />
                                                <span>{u.displayName}</span>
                                            </div>
                                        ))}
                                        {validUsers.length === 0 && (
                                            <p className="no-data-message">No users available</p>
                                        )}
                                        {validUsers.length > 0 && searchResults.length === 0 && (
                                            <p className="no-data-message">No matching users to add</p>
                                        )}
                                    </div>
                                )}
                            </div>
                            {selectedUsers.length > 0 && (
                                <div className="user-selected-list">
                                    {selectedUsers.map(u => (
                                        <div key={u.userID} className="user-selected-item">
                                            <span className="user-selected-name">{u.displayName}</span>
                                            <button
                                                type="button"
                                                className="user-remove-btn"
                                                onClick={() => toggleUser(u.userID)}
                                                title="Remove"
                                            >
                                                <FaTimes />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })()}

                {/* Department selection */}
                {config.assignmentType === 'Department' && (
                    <div className="form-group">
                        <label>Department</label>
                        <select
                            value={config.assignedDepartmentId || ''}
                            onChange={(e) => onChange('assignedDepartmentId', parseInt(e.target.value) || null)}
                        >
                            <option value="">-- Select a department --</option>
                            {departments.map(d => (
                                <option key={d.departmentID} value={d.departmentID}>
                                    {d.departmentName}
                                </option>
                            ))}
                        </select>
                        <small>An action item will be created for every active member of this department</small>
                    </div>
                )}

                {/* Property role selection */}
                {config.assignmentType === 'PropertyRole' && (
                    <>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Property</label>
                                <select
                                    value={config.propertyId || ''}
                                    onChange={(e) => onChange('propertyId', parseInt(e.target.value) || null)}
                                >
                                    <option value="">-- Select a property --</option>
                                    {properties.map(p => (
                                        <option key={p.propertyID} value={p.propertyID}>
                                            {p.propertyName}
                                        </option>
                                    ))}
                                </select>
                                <small>The property where users with the selected role will be assigned the action item.</small>
                            </div>
                            <div className="form-group">
                                <label>Role</label>
                                <select
                                    value={config.propertyRoleName || ''}
                                    onChange={(e) => onChange('propertyRoleName', e.target.value)}
                                >
                                    <option value="">-- Select a role --</option>
                                    <option value="Staff">Staff</option>
                                    <option value="CentreManager">Centre Manager</option>
                                    <option value="RegionalOperationsManager">Regional Operations Manager</option>
                                </select>
                                <small>Assigns to users holding this role at the selected property</small>
                            </div>
                        </div>
                        {config.propertyId && config.propertyRoleName && (
                            <div className="role-users-preview">
                                <label>Users with this role</label>
                                {loadingRoleUsers ? (
                                    <p className="no-data-message"><FaSpinner className="fa-spin" /> Loading...</p>
                                ) : roleUsers.length > 0 ? (
                                    <div className="user-selected-list">
                                        {roleUsers.map(m => (
                                            <div key={m.id} className="user-selected-item">
                                                <span className="user-selected-name">{m.userName}</span>
                                                <span className="user-selected-email">{m.userEmail}</span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="no-data-message">No users hold this role at the selected property</p>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
        </>
    );
};

/**
 * RunSqlReportConfig Component
 * Configuration form for RunSqlReport task type.
 * Includes real-time SQL validation to ensure only SELECT queries are allowed.
 */
const RunSqlReportConfig = ({ config, onChange, onValidateSql, databases, emailTemplates }) => {
    // State for SQL validation feedback
    const [sqlValidation, setSqlValidation] = useState({ isValid: true, error: null });

    /**
     * Handle SQL query change with validation.
     */
    const handleSqlChange = async (value) => {
        onChange('sqlQuery', value);
        // Validate the SQL query in real-time with the appropriate dialect
        if (value.trim()) {
            const validation = await onValidateSql(value, config.databaseSecretKey);
            setSqlValidation(validation);
        } else {
            setSqlValidation({ isValid: true, error: null });
        }
    };

    /**
     * Re-validate SQL when database changes (dialect might change).
     */
    const handleDatabaseChange = async (value) => {
        onChange('databaseSecretKey', value);
        // Re-validate existing SQL query with new dialect
        if (config.sqlQuery && config.sqlQuery.trim()) {
            const validation = await onValidateSql(config.sqlQuery, value);
            setSqlValidation(validation);
        }
    };

    return (
        <>
            <div className="form-row">
                <div className="form-group">
                    <label>Report Name</label>
                    <input
                        type="text"
                        value={config.reportName || ''}
                        onChange={(e) => onChange('reportName', e.target.value)}
                        placeholder="Weekly Sales Summary"
                    />
                </div>
                <div className="form-group">
                    <label>Database *</label>
                    <select
                        value={config.databaseSecretKey || ''}
                        onChange={(e) => handleDatabaseChange(e.target.value)}
                        required
                    >
                        <option value="">-- Select Database --</option>
                        {databases && databases.length > 0 ? (
                            databases.map(db => (
                                <option key={db.secretKey} value={db.secretKey}>
                                    {db.displayName || db.secretKey} {db.secretType ? `(${db.secretType})` : ''}
                                </option>
                            ))
                        ) : (
                            <option value="" disabled>No databases configured. Add database connections in Secrets.</option>
                        )}
                    </select>
                    {(!databases || databases.length === 0) && (
                        <small className="warning-text">
                            <FaExclamationTriangle /> No database connections found. Please configure database connection strings in System Settings → App Secrets.
                        </small>
                    )}
                </div>
            </div>

            <div className="form-group">
                <label>SQL Query *</label>
                <textarea
                    value={config.sqlQuery || ''}
                    onChange={(e) => handleSqlChange(e.target.value)}
                    placeholder="SELECT column1, column2 FROM table_name WHERE condition..."
                    rows={6}
                    className={`code-input ${!sqlValidation.isValid ? 'input-error' : ''}`}
                    required
                />
                {sqlValidation.isValid ? (
                    <small className="success-text">
                        <FaShieldAlt /> Only SELECT queries are allowed for security. INSERT, UPDATE, DELETE, and other data-modifying statements are blocked.
                    </small>
                ) : (
                    <small className="error-text">
                        <FaExclamationCircle /> {sqlValidation.error}
                    </small>
                )}
            </div>

            <div className="form-row">
                <div className="form-group">
                    <label>Output Format</label>
                    <select
                        value={config.outputFormat || 'CSV'}
                        onChange={(e) => onChange('outputFormat', e.target.value)}
                    >
                        {OUTPUT_FORMATS.map(f => (
                            <option key={f.value} value={f.value}>{f.label}</option>
                        ))}
                    </select>
                </div>
                <div className="form-group">
                    <label>Max Rows</label>
                    <input
                        type="number"
                        value={config.maxRows || 10000}
                        onChange={(e) => onChange('maxRows', parseInt(e.target.value) || 10000)}
                        min="1"
                        max="100000"
                    />
                </div>
            </div>

            <div className="form-group">
                <label>Email Template</label>
                <select
                    value={config.emailTemplateKey || ''}
                    onChange={(e) => onChange('emailTemplateKey', e.target.value)}
                >
                    <option value="">-- Use Plain Text (No Template) --</option>
                    {emailTemplates && emailTemplates.map(tpl => (
                        <option key={tpl.templateKey} value={tpl.templateKey}>
                            {tpl.displayName} {tpl.isDefault ? '(Default)' : ''}
                        </option>
                    ))}
                </select>
                <small>Select an email template for nicely formatted HTML emails with the report attached</small>
            </div>

            {/* SharePoint Integration */}
            <div className="form-section">
                <h4><FaShareAlt /> SharePoint Integration</h4>
                <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                        <input
                            type="checkbox"
                            checked={config.saveToSharePoint ?? false}
                            onChange={(e) => onChange('saveToSharePoint', e.target.checked)}
                        />
                        Save report to SharePoint automatically on each task run
                    </label>
                    <small>The task's main schedule controls how often the file is saved.</small>
                </div>

                {config.saveToSharePoint && (
                    <div className="form-row">
                        <div className="form-group">
                            <label>SharePoint File Name</label>
                            <input
                                type="text"
                                value={config.sharePointFileName || ''}
                                onChange={(e) => onChange('sharePointFileName', e.target.value)}
                                placeholder="CRM Case Dump"
                            />
                            <small>No date suffix — the same file is overwritten each run (ideal for Power BI live data).</small>
                        </div>
                        <div className="form-group">
                            <label>Folder Path Override <span className="optional-label">(optional)</span></label>
                            <input
                                type="text"
                                value={config.sharePointFolderPath || ''}
                                onChange={(e) => onChange('sharePointFolderPath', e.target.value)}
                                placeholder="POWER BI/Captrack dumps"
                            />
                            <small>Leave blank to use the default configured folder.</small>
                        </div>
                    </div>
                )}
            </div>

            <div className="form-group">
                <label>Recipient Emails {!config.saveToSharePoint && <span>*</span>}</label>
                <input
                    type="text"
                    value={config.recipientEmails || ''}
                    onChange={(e) => onChange('recipientEmails', e.target.value)}
                    placeholder="user@example.com, user2@example.com"
                    required={!config.saveToSharePoint}
                />
                <small>Comma-separated list of email addresses</small>
            </div>

            {config.saveToSharePoint && (
                <div className="form-group">
                    <label>Email Schedule <span className="optional-label">(optional)</span></label>
                    <select
                        value={config.emailCronExpression || ''}
                        onChange={(e) => onChange('emailCronExpression', e.target.value === '' ? null : e.target.value)}
                    >
                        <option value="">Send email on every task run</option>
                        <option value="0 8 * * 1-5">Weekdays at 8:00 AM</option>
                        <option value="0 8 * * 1">Every Monday at 8:00 AM</option>
                        <option value="0 8 * * *">Every day at 8:00 AM</option>
                        <option value="0 8 1 * *">First of every month at 8:00 AM</option>
                    </select>
                    <small>Controls how often the email is sent. The file is always saved to SharePoint on every task run regardless.</small>
                </div>
            )}

            {/* Only show subject/body fields if no template is selected */}
            {!config.emailTemplateKey && (
                <div className="form-group">
                    <label>Email Subject</label>
                    <input
                        type="text"
                        value={config.emailSubject || ''}
                        onChange={(e) => onChange('emailSubject', e.target.value)}
                        placeholder="Scheduled Report: {ReportName} - {Date}"
                    />
                </div>
            )}

            <div className="form-row checkbox-row">
                <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                        <input
                            type="checkbox"
                            checked={config.attachReport ?? true}
                            onChange={(e) => onChange('attachReport', e.target.checked)}
                        />
                        Attach report file to email
                    </label>
                </div>
                <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                        <input
                            type="checkbox"
                            checked={config.sendIfEmpty ?? false}
                            onChange={(e) => onChange('sendIfEmpty', e.target.checked)}
                        />
                        Send email even if no data
                    </label>
                </div>
            </div>
        </>
    );
};

/**
 * GeneratePdfReportConfig Component
 * Configuration form for GeneratePdfReport task type.
 */
const GeneratePdfReportConfig = ({ config, onChange }) => {
    return (
        <>
            <div className="form-group">
                <label>Report Type</label>
                <select
                    value={config.reportType || 'TenantStatements'}
                    onChange={(e) => onChange('reportType', e.target.value)}
                >
                    <option value="TenantStatements">Tenant Statements</option>
                    <option value="FinancialReport">Financial Report</option>
                    <option value="ComplianceReport">Compliance Report</option>
                </select>
            </div>

            <div className="form-group">
                <label>Recipients</label>
                <input
                    type="text"
                    value={config.recipients?.join(', ') || ''}
                    onChange={(e) => onChange('recipients', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                    placeholder="email1@example.com, email2@example.com"
                />
            </div>

            <div className="form-group checkbox-group">
                <label className="checkbox-label">
                    <input
                        type="checkbox"
                        checked={config.includeAttachments ?? false}
                        onChange={(e) => onChange('includeAttachments', e.target.checked)}
                    />
                    Include attachments
                </label>
            </div>
        </>
    );
};

export default ScheduledTasksPage;
